import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link as RouterLink } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import Box from '@material-ui/core/Box'
import TextField from '@material-ui/core/TextField'
import FormHelperText from '@material-ui/core/FormHelperText'
import Button from '@material-ui/core/Button'
import * as Cognito from '../../lib/cognito-api'
import { reportError } from '../../lib/raygun'
import ResendConfirmationCode from './resend-confirmation-code'
import Link from '@material-ui/core/Link'
import { formSchema } from './validation'
import { useStyles } from './styles'

const ConfirmAccountForm = ({ username, code, onSuccess }) => {
  const classes = useStyles()
  const [submissionError, setSubmissionError] = useState(null)

  const { register, handleSubmit, errors, formState } = useForm({
    mode: 'onTouched',
    resolver: yupResolver(formSchema),
    defaultValues: {
      confirmationCode: code || '',
    },
  })
  const { isSubmitting } = formState

  const alreadyConfirmedErrorLink = (username) => {
    return (
      <>
        <FormHelperText error={true}>This user has already been confirmed. Please log in or reset your password.</FormHelperText>
        <Link style={{ textAlign: 'center' }} component={RouterLink} to={`/login?username=${encodeURIComponent(username)}`}>
          Log In
        </Link>
      </>
    )
  }

  const onFormSubmit = async (formValues) => {
    try {
      setSubmissionError(null)
      await Cognito.confirmSignUp({
        username: username,
        confirmationCode: formValues.confirmationCode.trim(),
      })
      onSuccess && onSuccess()
    } catch (error) {
      if (error.message === 'User cannot be confirmed. Current status is CONFIRMED') {
        setSubmissionError(alreadyConfirmedErrorLink(username))
      } else if (error.__type === 'InvalidParameterException') {
        setSubmissionError('Incorrect verification code. Please try again.')
      } else {
        switch (error.code) {
          case 'CodeMismatchException':
            setSubmissionError(error.message)
            break
          default:
            setSubmissionError('An unexpected error occurred. Please try again.')
            reportError(error)
            break
        }
      }
    }
  }

  useEffect(() => {
    // auto-verify if code provided via query string
    if (code) {
      handleSubmit(onFormSubmit)()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  return (
    <>
      <Box mb={2}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <TextField
            className={classes.formField}
            name="confirmationCode"
            label="Verification code"
            inputRef={register}
            error={!!errors.confirmationCode}
            helperText={errors.confirmationCode && errors.confirmationCode.message}
          />
          <Button
            fullWidth
            size="large"
            variant="contained"
            color="primary"
            type="submit"
            disabled={isSubmitting}
          >
            Confirm Account
          </Button>
          {submissionError && <FormHelperText error={true}>{submissionError}</FormHelperText>}
        </form>
      </Box>
      <ResendConfirmationCode username={username} />
    </>
  )
}

export default ConfirmAccountForm
